import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  getFollowing, followUser, unfollowUser, searchUsers,
  getUserPreferences, saveUserPreferences,
} from '../../services/api';
import styles from './FollowManager.module.css';

export default function FollowManager() {
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // search-to-follow
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [busyIds, setBusyIds] = useState({}); // userId -> true while following/unfollowing

  // email preference
  const [prefs, setPrefs] = useState(null);
  const [emailOn, setEmailOn] = useState(true);
  const [savingPref, setSavingPref] = useState(false);

  const debounceRef = useRef(null);
  const searchWrapperRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [list, p] = await Promise.all([getFollowing(), getUserPreferences()]);
      setFollowing(list);
      setPrefs(p || {});
      setEmailOn((p || {}).follow_email !== false);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const followingIds = new Set(following.map(u => u.id));

  const handleSearchChange = (value) => {
    setQuery(value);
    setDropdownOpen(true);
    clearTimeout(debounceRef.current);
    if (value.length < 2) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        setSuggestions(await searchUsers(value));
      } catch {
        setSuggestions([]);
      }
    }, 200);
  };

  const setBusy = (id, v) => setBusyIds(prev => ({ ...prev, [id]: v }));

  const handleFollow = async (user) => {
    setBusy(user.id, true);
    setError(null);
    try {
      await followUser(user.id);
      setFollowing(prev =>
        prev.some(u => u.id === user.id)
          ? prev
          : [{ id: user.id, email: user.email, display_name: user.display_name, created_at: new Date().toISOString() }, ...prev]
      );
      setSuggestions(prev => prev.map(u => u.id === user.id ? { ...u, is_following: true } : u));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(user.id, false);
    }
  };

  const handleUnfollow = async (userId) => {
    setBusy(userId, true);
    setError(null);
    try {
      await unfollowUser(userId);
      setFollowing(prev => prev.filter(u => u.id !== userId));
      setSuggestions(prev => prev.map(u => u.id === userId ? { ...u, is_following: false } : u));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(userId, false);
    }
  };

  const handleToggleEmail = async () => {
    const next = !emailOn;
    setEmailOn(next);
    setSavingPref(true);
    try {
      const merged = { ...(prefs || {}), follow_email: next };
      const saved = await saveUserPreferences(merged);
      setPrefs(saved || merged);
    } catch (e) {
      setEmailOn(!next); // revert on failure
      setError(e.message);
    } finally {
      setSavingPref(false);
    }
  };

  if (loading) return <div className={styles.empty}>Loading…</div>;

  return (
    <div className={styles.root}>
      {error && <div className={styles.errorBanner}>{error}</div>}

      <label className={styles.prefRow}>
        <input
          type="checkbox"
          checked={emailOn}
          disabled={savingPref}
          onChange={handleToggleEmail}
        />
        <span>Email me when people I follow share a session (and when someone follows me)</span>
      </label>

      <div className={styles.searchWrapper} ref={searchWrapperRef}>
        <input
          className={styles.searchInput}
          type="text"
          placeholder="Find people to follow by name or email…"
          value={query}
          autoComplete="off"
          onChange={e => handleSearchChange(e.target.value)}
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
        />
        {dropdownOpen && suggestions.length > 0 && (() => {
          const rect = searchWrapperRef.current?.getBoundingClientRect();
          if (!rect) return null;
          return createPortal(
            <ul
              className={styles.dropdown}
              style={{ top: rect.bottom + 4, left: rect.left, width: rect.width }}
            >
              {suggestions.map(u => {
                const isFollowing = u.is_following || followingIds.has(u.id);
                return (
                  <li key={u.id} className={styles.dropdownItem}>
                    <div className={styles.dropdownMeta}>
                      <span className={styles.dropdownName}>{u.display_name || u.email}</span>
                      {u.display_name && <span className={styles.dropdownEmail}>{u.email}</span>}
                    </div>
                    <button
                      className={isFollowing ? styles.followingBtn : styles.followBtn}
                      disabled={busyIds[u.id]}
                      onMouseDown={e => {
                        e.preventDefault();
                        isFollowing ? handleUnfollow(u.id) : handleFollow(u);
                      }}
                    >
                      {busyIds[u.id] ? '…' : isFollowing ? 'Following' : 'Follow'}
                    </button>
                  </li>
                );
              })}
            </ul>,
            document.body
          );
        })()}
      </div>

      <div className={styles.sectionLabel}>
        Following ({following.length})
      </div>
      {following.length === 0 ? (
        <div className={styles.empty}>You're not following anyone yet.</div>
      ) : (
        <div className={styles.list}>
          {following.map(u => (
            <div key={u.id} className={styles.row}>
              <div className={styles.rowMeta}>
                <span className={styles.rowName}>{u.display_name || u.email}</span>
                {u.display_name && <span className={styles.rowEmail}>{u.email}</span>}
              </div>
              <button
                className={styles.unfollowBtn}
                disabled={busyIds[u.id]}
                onClick={() => handleUnfollow(u.id)}
              >
                {busyIds[u.id] ? '…' : 'Unfollow'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
